<!-- template is directly on the cshtml page type 'ModelOverviewPage' -->
<script>
    import _ from 'lodash';
    import axios from 'axios';
    import StockListModelOverviewFilterPanel from './StockListModelOverviewFilterPanel.vue';
    import StockListModelOverviewTableContainer from './StockListModelOverviewTableContainer.vue';
    import { enableSpinner, disableSpinner } from '../js/spinner.js';
    import formatMixin from '../mixins/formatter.js';

    export default {
        name: 'stocklist-model-overview',
        template: '#stock-list-model-overview-page',
        mixins: [formatMixin],
        props: {
            pTableData: {
                type: Array,
                default: []
            }
        },
        data() {
            return {
                tableData: this.pTableData,
                selectedBrands: [],
                selectedModels: [],
                selectedPropellants: [],
                selectedCO2: [],
                selectedTaxBase: [],
                selectedTowing: []
            }
        },
        computed: {
            brandOptions: function () {
                if (this.tableData) {
                    return this.tableData.map(b => ({ text: this.formatBrand(b.brand), value: b.brand }))
                }

                return undefined;
            },
            modelOptions: function () {
                let vm = this;
                if (this.tableData) {
                    let modelOptions = this.tableData.map(table => {
                        if (vm.selectedBrands.includes(table.brand) || vm.selectedBrands.length == 0) {
                            let uniqueCarModels = _.uniqBy(table.carModels, carModel => carModel.modelType);
                            let options = uniqueCarModels.map(carmodel => ({ text: carmodel.modelType, value: carmodel.modelType }));

                            options.sort((a, b) => a.text.localeCompare(b.text));

                            return ({ brand: table.brand, modelOptions: options });
                        }
                    });

                    //remove undefined objects
                    return modelOptions.filter(option => option !== undefined);
                }

                return undefined;
            },
            propellantOptions: function () {
                let vm = this;
                if (this.tableData) {
                    let allPropellantTypes = this.tableData
                        .flatMap(table => table.carModels)
                        .flatMap(carmodel => carmodel.propellant)
                        .map(propellant => ({ text: this.setPropellantText(propellant), value: propellant }));

                    let uniquePropellantTypes = allPropellantTypes.filter((v, i, a) => a.findIndex(t => (t.value === v.value)) === i);

                    return uniquePropellantTypes;
                }

                return undefined;
            },
            taxBaseRange: function () {
                if (this.tableData) {
                    let allCarModels = _.flatMap(this.tableData, table => table.carModels);
                    let allTaxBase = allCarModels.map(carmodel => carmodel.taxBase);

                    let min = _.min(allTaxBase);
                    let max = _.max(allTaxBase);

                    //round to nearest 10.000
                    min = _.floor(min, -4);
                    max = _.ceil(max, -4);

                    return { min: min, max: max };
                }

                return undefined;
            },
            co2Range: function () {
                if (this.tableData) {
                    let allCarModels = _.flatMap(this.tableData, table => table.carModels);
                    let allCo2 = _.flatMap(allCarModels, carmodel => carmodel.co2);

                    let min = _.min(allCo2);
                    let max = _.max(allCo2);

                    return { min: min, max: max };
                }

                return undefined;
            },
            towingRange: function () {
                if (this.tableData) {
                    let allCarModels = _.flatMap(this.tableData, table => table.carModels);
                    let allTowing = _.flatMap(allCarModels, carmodel => carmodel.maxTowingCapacity)

                    let min = _.min(allTowing);
                    let max = _.max(allTowing);

                    return { min: min, max: max };
                }
            },
            filteredTableData: function () {
                let vm = this;
                if (this.tableData) {
                    // Create a deep copy to avoid mutating the original data
                    let newTableData = JSON.parse(JSON.stringify(this.tableData));

                    //filter by selected brands
                    if (this.selectedBrands.length > 0) {
                        newTableData = newTableData.filter(table => {
                            return vm.selectedBrands.includes(table.brand);
                        });
                    }

                    //filter by selected models
                    if (this.selectedModels.length > 0) {
                        newTableData = newTableData.map(table => (
                            {
                                brand: table.brand,
                                carModels: table.carModels.filter(carModel => vm.selectedModels.includes(carModel.modelType))
                            }));
                    }

                    //filter by propellant
                    if (this.selectedPropellants.length > 0) {
                        newTableData = newTableData.map(table => (
                            {
                                brand: table.brand,
                                carModels: table.carModels.filter(carModel => vm.selectedPropellants.includes(carModel.propellant))
                            }));
                    }

                    //filter by taxbase
                    if (this.selectedTaxBase.length > 0) {
                        newTableData = newTableData.map(table => (
                            {
                                brand: table.brand,
                                carModels: table.carModels.filter(carModel => carModel.taxBase >= vm.selectedTaxBase[0] && carModel.taxBase <= vm.selectedTaxBase[1])
                            }));
                    }

                    //filter by co2
                    if (this.selectedCO2.length > 0) {
                        newTableData = newTableData.map(table => (
                            {
                                brand: table.brand,
                                carModels: table.carModels.filter(carModel => vm.isCo2InSelectedRange(carModel.co2))
                            }));
                    }

                    if (this.selectedTowing.length > 0) {
                        newTableData = newTableData.map(table => ({
                            brand: table.brand,
                            carModels: table.carModels.filter(carModel => carModel.maxTowingCapacity >= vm.selectedTowing[0])
                        }));
                    }

                    return newTableData;
                }

                return undefined;
            }
        },
        components: {
            StockListModelOverviewFilterPanel,
            StockListModelOverviewTableContainer
        },
        methods: {
            isCo2InSelectedRange: function (co2) {
                if (co2 <= this.selectedCO2) {
                    return true;
                }

                return false;
            },
            setPropellantText: function (value) {
                if (value === "gasoline" || value === "benzin") {
                    return "Benzin";
                } else if (value === "electric") {
                    return "El";
                } else if (value === "diesel") {
                    return "Diesel";
                } else if (value === "hybrid") {
                    return "Hybrid";
                }

                return "unknown"
            },
            receiveSelectedBrands: function (value) {
                this.$data.selectedBrands = value;
                this.savePageState();
            },
            receiveSelectedModels: function (value) {
                this.$data.selectedModels = value;
                this.savePageState();
            },
            receiveSelectedPropellants: function (value) {
                this.$data.selectedPropellants = value;
                this.savePageState();
            },
            receiveSelectedTaxBase: function (value) {
                this.$data.selectedTaxBase = value;
                this.savePageState();
            },
            receiveSelectedCo2: function (value) {
                this.$data.selectedCO2 = value;
                this.savePageState();
            },
            receiveSelectedTowing: function (value) {
                this.$data.selectedTowing = value;
                this.savePageState();
            },
            savePageState: function () {
                localStorage.selectedBrands = this.selectedBrands;
                localStorage.selectedModels = this.selectedModels;
                localStorage.selectedPropellants = this.selectedPropellants;
                localStorage.selectedCO2 = this.selectedCO2;
                localStorage.selectedTaxBase = this.selectedTaxBase;
                localStorage.selectedTowing = this.selectedTowing;
            },
            clearPageState: function () {
                localStorage.clear();
            }
        }
    }
</script>